import { useEffect, useRef, useState } from 'react';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import SelectField from '../../../components/Forms/Input/SelectField';
import { ivaTypeOptions } from '../productData';
import { calculateTaxFromUnitCostAndQty } from '../../../utils/tax/creditTaxCalculator';
import SvgIcon from '../../SvgIcon/SvgIcon';
import { useEnterToClick } from '../../../hooks/useEnter';

interface Props {
  productToEdit: any; // { id, productId, productName, unit, unitCost, quantity, ivaTotal, totalCost, ... }
  productOptions: { id: number; name: string; unit?: string }[];
  onUpdate: (product: any) => void;
  onClose: () => void;
}

const EditBasicProductForm = ({
  productToEdit,
  productOptions,
  onUpdate,
  onClose,
}: Props) => {
  const saveBtnRef = useRef<HTMLButtonElement>(null);
  useEnterToClick(saveBtnRef, true);

  const [form, setForm] = useState({
    productId: '',
    productName: '',
    ivaType: 'GRAVADO', // fijo
    unit: '',
    unitCost: '',
    quantity: '',
    ivaTotal: '',
    totalCost: '',
    withIva: '',
    ivaUnit: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Precarga desde BD (una sola vez por cambio de productoToEdit)
  useEffect(() => {
    if (!productToEdit) return;

    const unitCost = productToEdit.unitCost ?? 0;
    const quantity = productToEdit.quantity ?? 0;

    // Calcula derivados a partir de lo que viene de BD
    const { unitCostWithIva, ivaTotal, totalCost, ivaUnit } =
      calculateTaxFromUnitCostAndQty(
        Number(unitCost) || 0,
        Number(quantity) || 0
      );

    setForm({
      productId: String(productToEdit.productId ?? ''),
      productName: productToEdit.productName ?? '',
      ivaType: 'GRAVADO',
      unit: productToEdit.unit ?? '',
      unitCost: String(unitCost ?? ''),
      quantity: String(quantity ?? ''),
      ivaTotal: String(ivaTotal ?? ''),
      totalCost: String(totalCost ?? ''),
      withIva: String(unitCostWithIva ?? ''),
      ivaUnit: String(ivaUnit ?? ''),
    });
  }, [productToEdit]);

  // Solo dejamos editar la cantidad
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, quantity: value }));

    const qty = parseFloat(value);
    const unitCost = prevSafe(
      (prev) => prev.unitCost,
      (value) => value
    );

    if (
      Number.isFinite(qty) &&
      qty > 0 &&
      Number.isFinite(unitCost) &&
      unitCost > 0
    ) {
      const { unitCostWithIva, ivaTotal, totalCost, ivaUnit } =
        calculateTaxFromUnitCostAndQty(unitCost, qty);

      setForm((prev) => ({
        ...prev,
        withIva: String(unitCostWithIva),
        ivaUnit: String(ivaUnit),
        ivaTotal: String(ivaTotal),
        totalCost: String(totalCost),
      }));
    } else {
      // Si la cantidad no es válida, “resetea” derivados (o déjalos como venían)
      setForm((prev) => ({
        ...prev,
        withIva: prev.withIva,
        ivaUnit: prev.ivaUnit,
        ivaTotal: prev.ivaTotal,
        totalCost: prev.totalCost,
      }));
    }
  };

  // Helper para leer un valor numérico seguro del estado
  function prevSafe<T>(
    getter: (f: typeof form) => string,
    _map: (s: string) => T
  ): number {
    const v = getter(form);
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }

  const handleSave = () => {
    const errs: Record<string, string> = {};
    if (!form.productId) errs.productId = 'Producto requerido';
    if (!form.unitCost) errs.unitCost = 'Costo unitario requerido';
    if (!form.quantity) errs.quantity = 'Cantidad requerida';

    setErrors(errs);
    if (Object.keys(errs).length) return;

    const updatedProduct = {
      ...productToEdit,
      detailId: productToEdit.id, // mantienes tu convención
      productId: Number(form.productId),
      productName: form.productName,
      unit: form.unit,
      unitCost: Number(form.unitCost),
      quantity: Number(form.quantity),
      ivaTotal: Number(form.ivaTotal || 0),
      totalCost: Number(form.totalCost || 0),
      taxType: form.ivaType === 'GRAVADO' ? 'TAXED' : 'EXEMPT',
    };

    onUpdate(updatedProduct);
    onClose();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Producto: solo mostrar/disabled (ya viene de BD) */}
        <SimpleSearchSelect
          label="Producto"
          name="productId"
          iconKey="category"
          options={productOptions}
          selectedValue={Number(form.productId)}
          onSelect={() => {}}
          error={errors.productId}
          disabled
          required
        />

        <SelectField
          label="Tipo"
          name="ivaType"
          iconKey="list"
          value={form.ivaType}
          onChange={() => {}}
          options={ivaTypeOptions}
          disabled
          required
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {/* UnitCost fijo (solo lectura) */}
        <InputField
          label="Costo Unitario"
          name="unitCost"
          iconKey="dollar"
          type="number"
          value={form.unitCost}
          onChange={() => {}}
          error={errors.unitCost}
          disabled
        />

        {/* La ÚNICA editable: Cantidad */}
        <InputField
          label="Cantidad"
          name="quantity"
          iconKey="product"
          type="number"
          value={form.quantity}
          onChange={handleQuantityChange}
          error={errors.quantity}
          min={0}
          required
        />

        <InputField
          label="IVA Total"
          name="ivaTotal"
          iconKey="dollar"
          type="number"
          value={form.ivaTotal}
          onChange={() => {}}
          disabled
        />
        <InputField
          label="Costo Total"
          name="totalCost"
          iconKey="dollar"
          type="number"
          value={form.totalCost}
          onChange={() => {}}
          disabled
        />
      </div>

      <div className="text-gray-600 space-y-1 text-sm dark:text-white">
        <p>CON IVA: $ {form.withIva || '0.00000000'}</p>
        <p>IVA UNITARIO: $ {form.ivaUnit || '0.00000000'}</p>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          ref={saveBtnRef}
          onClick={handleSave}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="save" className="h-5 w-5" />
          Actualizar
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-2 rounded-md bg-[#DC3545] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="cancel" className="h-5 w-5" />
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default EditBasicProductForm;
